import { Box, Stack, Group, Text, Checkbox, Slider, Divider, Modal, NumberInput, Button, ActionIcon, Menu, Select, SegmentedControl, Input, Tooltip } from '@mantine/core';
import Download from 'lucide-react/dist/esm/icons/download';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Folder from 'lucide-react/dist/esm/icons/folder';
import Film from 'lucide-react/dist/esm/icons/film';
import { InlineRow } from '../../../shared/components/InlineRow';
import type { GlobalSettings, MediaBinItem, HwCapabilities } from '../types';
import { SETTINGS_LABEL_W, SETTINGS_ROW_H, VOLUME_MIN, VOLUME_MAX } from '../utils';

interface Props {
  settings: GlobalSettings; hwCapabilities?: HwCapabilities | null; onSettingsChange: (p: Partial<GlobalSettings>) => void;
  onBrowseOutput: () => void; mediaBin: MediaBinItem[]; onCanvasFromBin: (i: MediaBinItem) => void;
  onExport: () => void; onImport: () => void; modalOpened: boolean; onCloseModal: () => void;
}

export function EditorSettings({ settings, hwCapabilities, onSettingsChange, onBrowseOutput, mediaBin, onCanvasFromBin, onExport, onImport, modalOpened, onCloseModal }: Props) {
  return (
    <Box mb="lg">
      <Stack gap="xs">
        <Group px={12} h={SETTINGS_ROW_H} gap="md" wrap="nowrap" align="center">
          <Text fz="md" fw={500} w={SETTINGS_LABEL_W} style={{ flexShrink: 0 }}>Вставить интро</Text>
          <Checkbox checked={settings.useIntro} onChange={e => onSettingsChange({ useIntro: e.currentTarget.checked })} />
        </Group>

        <Group px={12} h={SETTINGS_ROW_H} gap="md" wrap="nowrap" align="center">
          <Text fz="md" fw={500} w={SETTINGS_LABEL_W} style={{ flexShrink: 0 }}>Громкость</Text>
          <Slider style={{ flex: 1 }} min={VOLUME_MIN} max={VOLUME_MAX} step={0.1} label={v => `${v.toFixed(1)}x`} value={settings.volume} onChange={v => onSettingsChange({ volume: v })} />
          <Text fz="md" fw={600} c="dimmed" style={{ flexShrink: 0, width: 44, textAlign: 'right' }}>{settings.volume.toFixed(1)}x</Text>
        </Group>

        <Divider mt="xs" />
        <InlineRow label="Имя файла" labelWidth={SETTINGS_LABEL_W} labelSize="md" value={settings.outputFilename || null} placeholder="Введите имя файла" suffix=".mp4" onValueChange={v => onSettingsChange({ outputFilename: v })} />
        <InlineRow label="Папка сохранения" required labelWidth={SETTINGS_LABEL_W} labelSize="md" value={settings.outputDir ? settings.outputDir.replace(/^\/app\/data\//, '') : null} tooltip={settings.outputDir || undefined} placeholder="Выберите папку" onClick={onBrowseOutput} />
      </Stack>

      <Modal opened={modalOpened} onClose={onCloseModal} title="Параметры" centered>
        <Stack>
          <Input.Wrapper label="Холст" description="Разрешение и частота кадров итогового видео">
            <Group gap="xs" wrap="nowrap" align="center" mt={6}>
              <NumberInput w={84} min={16} step={2} hideControls value={settings.canvasWidth} onChange={v => onSettingsChange({ canvasWidth: Number(v) || 1920 })} aria-label="Ширина" />
              <Text c="dimmed">x</Text>
              <NumberInput w={84} min={16} step={2} hideControls value={settings.canvasHeight} onChange={v => onSettingsChange({ canvasHeight: Number(v) || 1080 })} aria-label="Высота" />
              <NumberInput w={96} min={1} step={1} suffix=" FPS" hideControls value={settings.canvasFps} onChange={v => onSettingsChange({ canvasFps: Number(v) || 60 })} aria-label="Частота кадров" />
              <Menu withinPortal position="bottom-end">
                <Menu.Target>
                  <span style={{ display: 'inline-flex' }}>
                    <Tooltip label="Взять размер и FPS из файла в корзине" withArrow openDelay={300}>
                      <ActionIcon variant="default" size="lg" disabled={mediaBin.length === 0} aria-label="Взять параметры из корзины"><Folder size={16} /></ActionIcon>
                    </Tooltip>
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Взять параметры из корзины</Menu.Label>
                  {mediaBin.map(i => (
                    <Menu.Item key={i.id} leftSection={<Film size={14} />} disabled={!i.width} onClick={() => onCanvasFromBin(i)}>
                      <Text fz="xs" truncate maw={480}>{i.name}{i.width ? ` — ${i.width}x${i.height}${i.fps ? `, ${Math.round(i.fps)} FPS` : ''}` : ''}</Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Input.Wrapper>

          <Input.Wrapper label="Кодирование" description={hwCapabilities?.can_gpu_encode ? `На видеокарте: ${hwCapabilities.gpu_encoder}` : 'GPU-кодирование недоступно'}>
            <SegmentedControl mt={6} display="flex" value={settings.encoderType || (hwCapabilities?.can_gpu_encode ? 'gpu' : 'cpu')} onChange={v => onSettingsChange({ encoderType: v as any })} data={[{ value: 'gpu', label: 'GPU', disabled: !hwCapabilities?.can_gpu_encode }, { value: 'cpu', label: 'CPU' }]} />
          </Input.Wrapper>
          {settings.encoderType === 'cpu' && (
            <Select label="Скорость CPU (x264 preset)" data={[{ value: 'medium', label: 'medium (~80 FPS)' }, { value: 'faster', label: 'faster (~200 FPS)' }, { value: 'ultrafast', label: 'ultrafast (~300+ FPS)' }]} value={settings.x264Preset || 'medium'} onChange={v => onSettingsChange({ x264Preset: (v as any) || 'medium' })} allowDeselect={false} />
          )}

                    <Checkbox
            label="Нормализация звука (EBU R128, -14 LUFS)"
            description="Автоматически приводит громкость в превью и финальном видео к стандарту YouTube/VK (-14 LUFS, True Peak -1 dBFS), убирая перепады."
            checked={Boolean(settings.normalizeLoudness)}
            onChange={e => onSettingsChange({ normalizeLoudness: e.currentTarget.checked })}
          />



          <NumberInput label="Дедлайн задачи (ч.)" description="Автоматически завершить задачу при зависании" min={0.1} max={24} step={1} value={settings.deadlineHours} onChange={v => onSettingsChange({ deadlineHours: Math.min(Number(v) || 24, 24) })} />

          <Divider label="Проект" labelPosition="center" mt="xs" />
          <Group grow>
            <Button variant="light" leftSection={<Upload size={16} />} onClick={() => { onImport(); onCloseModal(); }}>Импорт JSON</Button>
            <Button variant="light" leftSection={<Download size={16} />} onClick={() => { onExport(); onCloseModal(); }}>Экспорт JSON</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
